/**
 * Shared display maps for failure-diagnostic rendering.
 *
 * Single source for the category label/tone and root-cause-timeline severity
 * colors so the full `DiagnosticReport` (issue drawer), the compact
 * `DiagnosticHeadline` (run summary), and the video's diagnostic-timeline
 * markers can't drift apart.
 *
 * @module ui/components/issues/diagnosticDisplay
 */
import React from 'react';
import {
  Bug,
  ArrowsClockwise,
  Plug,
  Question,
  Warning,
  FileDashed,
  FileX,
  Stack,
} from '@phosphor-icons/react';
import type {
  FailureDiagnostic,
  FailureDiagnosticCategory,
  RootCauseTimelineEvent,
} from '@chatsdet/types/issues';

/** TagBadge color tokens used for diagnostic category badges. */
export type DiagnosticCategoryTone =
  | 'blue'
  | 'gray'
  | 'red'
  | 'orange'
  | 'teal'
  | 'yellow'
  | 'purple';

export const CATEGORY_LABELS: Record<FailureDiagnosticCategory, string> = {
  software_bug: 'Software Bug',
  dependency_issue: 'Dependency Issue',
  agent_confusion: 'Agent Confusion',
  platform_error: 'Platform Error',
  test_underspecified: 'Test Underspecified',
  data_collision: 'Data Collision',
  app_changed: 'App Changed',
  test_defect: 'Test Bug',
};

export const CATEGORY_TONE: Record<FailureDiagnosticCategory, DiagnosticCategoryTone> = {
  software_bug: 'red',
  dependency_issue: 'yellow',
  agent_confusion: 'blue',
  platform_error: 'gray',
  test_underspecified: 'yellow',
  data_collision: 'purple',
  app_changed: 'orange',
  test_defect: 'yellow',
};

/**
 * Leading icon per diagnostic category, sized/weighted to sit inside a
 * `TagBadge`'s `icon` slot (see `StatusIconBar`'s `statusIcon` map for the
 * same small-icon-in-a-badge precedent).
 */
export const CATEGORY_ICONS: Record<FailureDiagnosticCategory, React.ReactNode> = {
  software_bug: React.createElement(Bug, { size: 12, weight: 'bold' }),
  app_changed: React.createElement(ArrowsClockwise, { size: 12, weight: 'bold' }),
  dependency_issue: React.createElement(Plug, { size: 12, weight: 'bold' }),
  agent_confusion: React.createElement(Question, { size: 12, weight: 'bold' }),
  platform_error: React.createElement(Warning, { size: 12, weight: 'bold' }),
  test_underspecified: React.createElement(FileDashed, { size: 12, weight: 'bold' }),
  data_collision: React.createElement(Stack, { size: 12, weight: 'bold' }),
  test_defect: React.createElement(FileX, { size: 12, weight: 'bold' }),
};

/**
 * Compact classification label for the run-detail diagnostic surfaces. A
 * corrected outcome is intentionally visible only when the backend records
 * the durable correction — ordinary diagnostics keep their existing label.
 */
export function diagnosticClassificationLabel(
  diagnostic: Pick<FailureDiagnostic, 'category' | 'outcomeCorrection'>
): string {
  const categoryLabel = CATEGORY_LABELS[diagnostic.category];
  if (!diagnostic.outcomeCorrection) return categoryLabel;
  return `${categoryLabel} · Failed → Passed`;
}

type RootCauseSeverity = NonNullable<RootCauseTimelineEvent['severity']>;

/** Tailwind background class for a root-cause-timeline marker/dot (defaults to info). */
export function rootCauseSeverityDotClass(severity: RootCauseTimelineEvent['severity']): string {
  switch (severity) {
    case 'critical':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    case 'info':
    default:
      return 'bg-blue-500';
  }
}

/**
 * Hex color for a root-cause event when rendered as a video timeline marker
 * (`annotationColor`). Mirrors {@link rootCauseSeverityDotClass}.
 */
export function rootCauseSeverityHex(severity: RootCauseTimelineEvent['severity']): string {
  const map: Record<RootCauseSeverity, string> = {
    critical: '#ef4444', // red-500
    warning: '#f59e0b', // amber-500
    info: '#3b82f6', // blue-500
  };
  return map[severity ?? 'info'];
}
